import { colors } from '@kmns-internal/furikake/tokens'
import { LocationOn, OpenInNew } from '@mui/icons-material'
import { Divider, Link, Stack, Typography } from '@mui/material'
import { AppRoleEnum, PlaceNodeTypeEnum } from '@ulysses-inc/harami_api_client'
import type React from 'react'
import { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PlaceGroupSVG from 'src/assets/icons/place_groups.svg?react'
import { getKaminashiIDBasePath } from 'src/config'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import { useQueryLoginUserContext } from 'src/features/tanStackQuery/commonQueries/useQueryLoginUserContext'
import {
  changeIsShowEditUserDrawer,
  changeUpdatedUsersFlag,
} from 'src/state/ducks/users/actions'
import type { RootState } from 'src/state/store'
import FooterButton from 'src/views/components/drawer/FooterButton'
import { roleOptions } from '../roleOptions'
import { getRenderSelect } from './EditUser.components'
import { useFormikProps } from './EditUser.hooks'

export interface UserForm {
  role: AppRoleEnum
  groupIds: string[]
  placeNodeIds: string[]
}

const EditUser: React.FC = () => {
  const activeUser = useSelector(({ usersState }: RootState) => {
    const activeUserId = usersState.users.activeUserId
    return activeUserId
      ? usersState.users.users.find(({ uuid }) => uuid === activeUserId)
      : undefined
  })
  const userGroups = useSelector(
    ({ usersState }: RootState) => usersState.userGroups.userGroups,
  )
  const placeNodes = useSelector(
    ({ placesState }: RootState) => placesState.placeNodes.nodes,
  )
  const isLoadingUsers = useSelector(
    ({ usersState }: RootState) => usersState.users.isLoading,
  )

  const { data: loginUserContext } = useQueryLoginUserContext()

  const linkToMemberEditPage = useMemo(() => {
    const rumpMemberId = activeUser?.rumpMemberId
    const rumpRealmId = loginUserContext?.rumpRealmId
    if (!rumpRealmId || !rumpMemberId) {
      return ''
    }

    const url = new URL(
      `/realms/${rumpRealmId}/members`,
      getKaminashiIDBasePath(),
    )
    url.searchParams.append('memberId', rumpMemberId)
    return url.toString()
  }, [activeUser, loginUserContext])

  const dispatch = useDispatch()
  const dispatchChangeIsShowEditUserDrawer = (isShow: boolean) =>
    dispatch(changeIsShowEditUserDrawer(isShow))
  const dispatchChangeUpdatedUsersFlag = (edited: boolean) =>
    dispatch(changeUpdatedUsersFlag(edited))

  const loginUserUuid = localStorage.getItem(localStorageKeys.loginUserUuid)
  const isEditingSelf = activeUser?.uuid === loginUserUuid

  const formikProps = useFormikProps()
  const renderSelect = getRenderSelect(formikProps)

  const { handleSubmit, values } = formikProps

  const onSubmit = () => {
    handleSubmit()
    dispatchChangeUpdatedUsersFlag(true)
  }

  return (
    <Stack sx={{ minHeight: '100vh' }}>
      <Typography variant="h6" sx={{ my: 2, px: 3 }}>
        {activeUser ? 'ユーザーの編集' : 'ユーザーの追加'}
      </Typography>
      <Divider />
      <Stack
        justifyContent="space-between"
        sx={{ display: 'flex', height: '100%' }}
      >
        <Stack sx={{ px: 3, py: 2 }} spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {activeUser?.name ?? ''}
          </Typography>
          {/* 管理者しかこのページはアクセスできない。自分を変更すると管理者でなくなった後画面が操作不能になるので、変更できないようにする */}
          {isEditingSelf && (
            <Stack spacing={1}>
              <Typography variant="subtitle1">ロール（役割）</Typography>
              <Typography variant="body2">
                自分のロール（役割）は変更できません。変更したい場合は、他の管理者にご依頼ください。
              </Typography>
              <input type="hidden" name="role" value={values.role} />
            </Stack>
          )}
          <Stack sx={{ display: isEditingSelf ? 'none' : 'block' }}>
            {renderSelect(
              'role',
              'ロール（役割）',
              roleOptions,
              activeUser?.role?.role ?? AppRoleEnum.REPORTER,
            )}
          </Stack>
          <Divider />
          {renderSelect(
            'groupIds',
            '所属するユーザーグループ',
            userGroups?.map(({ id, name }) => [`${id ?? ''}`, name ?? '']) ??
              [],
            activeUser?.userGroups?.map(({ id }) => `${id ?? ''}`),
            true,
          )}
          {renderSelect(
            'placeNodeIds',
            '所属する現場',
            placeNodes.map(({ uuid, type, place, placeGroup }) => [
              uuid,
              type === PlaceNodeTypeEnum.PlaceGroup
                ? (placeGroup?.name ?? '')
                : (place?.name ?? ''),
              type === PlaceNodeTypeEnum.PlaceGroup ? (
                <PlaceGroupSVG
                  key={uuid}
                  style={{ width: '16px', color: colors.action.active }}
                />
              ) : (
                <LocationOn
                  key={uuid}
                  sx={{ width: '16px', color: colors.action.active }}
                />
              ),
            ]),
            activeUser?.placeNodes?.map(({ uuid }) => uuid),
            true,
          )}
          <Typography variant="body2" sx={{ mt: 2 }}>
            {loginUserContext?.isRumpRealmAdmin ? (
              <>
                その他のユーザー情報の編集・ユーザーの削除は
                <Link
                  sx={{ mx: 0.5 }}
                  href={linkToMemberEditPage}
                  target="_blank"
                  rel="noreferrer"
                >
                  カミナシ ID管理
                  <OpenInNew
                    sx={{
                      display: 'inline-block',
                      ml: 0.5,
                      width: '12px',
                      verticalAlign: 'middle',
                    }}
                  />
                </Link>
                で行ってください。
              </>
            ) : (
              'その他のユーザー情報の編集・ユーザーの削除は管理者に依頼してください。'
            )}
          </Typography>
        </Stack>
        <Stack sx={{ px: 3, py: 2 }}>
          <FooterButton
            spinning={isLoadingUsers}
            onCancel={() => dispatchChangeIsShowEditUserDrawer(false)}
            onSubmit={onSubmit}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}

export default EditUser
